/*
  One set's cards and their population by grade (§6.2–§6.5, §7.3), rebuilt on the task-6
  primitives.

  It used to hold its own reducer, its own MUI Paper/Table/Skeleton and its own
  sort-then-slice — the sort mutating the array it was handed. All three now come from
  components/DataTable, so what is left is the columns and the fetch.

  Four things a reader should not have to reconstruct:

  - **This table fetches its own rows, so it owns its own error state** (§6.2, §12.20).
    Every other table is handed `error` by its view; SetView has nothing to pass because
    the query lives here, and the `error` this query returned was thrown away. The state
    machine is the same one (DataTable's `error` prop) — only the source differs.

  - **The sixteen grade columns sort by their count** (§6.4). They were `sortable: false`
    across the board. Their value is gradeCountValue(grade, finalGradeCounts), missing
    counting as 0 — the same function CardGradeViewLink displays, so the column cannot
    order by one number and paint another. They need a custom render AND a sort value,
    which is exactly why `render` is optional on the descriptor rather than required.

  - **"Card #" is NOT a numeric column.** Card numbers are strings; `numeric` drives
    formatCount, which would render every one of them as `0`. The ordering is
    compareByCardNumber instead, so they run 1, 2, 10, 100 rather than 1, 10, 100, 2
    (§6.4, §12.18). numeric is for counts.

  - **The grade filter is applied before selectTableRows.** D18's dropdown narrows the
    row set; the text filter, the sort and the pagination then all see the same rows, and
    the two filters compose instead of racing.
*/

import { ReactNode, useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import CardGradeViewLink from "components/CardGradeViewLink";
import CardThumbPreview from "components/CardThumbPreview";
import {
  DataTable,
  DataTableColumn,
  DataTableEmpty,
  DataTablePagination,
  TableController,
  TableSortState,
  compareByCardNumber,
  selectTableRows,
} from "components/DataTable";
import { FOCUS_RING, LINK } from "components/styles";

import {
  filterCardsGraded,
  LOWER_GRADE_COLUMN_IDS,
  visibleGradeColumns,
} from "constants/grades";

import { sanitizeURIPathSegment, sanitizeURISlug } from "helpers/routing";
import { formatCount, gradeCountValue } from "lib";

import { useGetProductsWithCountsQuery } from "services/pcgPublicV2";
import { CardInfo } from "types/gameInfo";

/*
  A grade cell's count on a gold pill whose strength is that grade's share of the card's
  grades, so each row reads as a heat strip: where a card's grades pile up shows before a
  single number is read. Capped so the gold link text stays readable on the strongest
  pill. Zero renders nothing, as the empty cell always has.
*/
const GradePill = ({ count, total, children }: { count: number; total: number; children: ReactNode }) => {
  if (!count) {
    return null;
  }
  const share = total > 0 ? Math.min(1, count / total) : 0;
  return (
    <span
      className="inline-block min-w-[2.25rem] rounded-md px-1.5 py-1 text-right tabular-nums"
      style={{ backgroundColor: `rgb(var(--pcg-gold) / ${(0.06 + 0.26 * share).toFixed(3)})` }}
    >
      {children}
    </span>
  );
};

/*
  The v1 set endpoint's products in the table's row shape. Exported because SetView feeds
  the same rows to its header and its top-cards shelf, from the same (deduplicated) query.
*/
export const productsToCards = (data: any[], gameCode: string): CardInfo[] =>
  data.map((product: any) => ({
    id: product.id,
    cardName: product.cardName || product.title,
    number: product.number,
    title: product.title || product.cardName,
    rarity: product.rarity || '',
    image: product.image,
    setName: product.setName,
    gameCode: product.gameCode || gameCode,
    cardsGraded: product.cardsGraded,
    /*
      Defaulted, not left null: filterCardsGraded reads .length off it, and a card with
      no counts at all is a real row this table has always listed.
    */
    finalGradeCounts: product.finalGradeCounts || [],
    binderId: product.binderId || '',
    releaseDate: product.releaseDate || '',
    archived: product.archived || false,
    reviewed: product.reviewed || true,
  }));

/* "holo" -> "Holo". Empty stays empty, so no blank line is rendered under the name. */
const titleCaseRarity = (rarity: string): string =>
  rarity ? rarity.charAt(0).toUpperCase() + rarity.slice(1) : "";

/*
  gameCode, setName and showAll are parameters rather than props read inside a render
  callback, so the list can be memoised on them — a fresh array every render would re-run
  selectTableRows every render.
*/
export const setViewColumns = (
  gameCode: string,
  setName: string,
  showAll: boolean
): DataTableColumn<CardInfo>[] => {
  const cardPath = (row: CardInfo) =>
    `/${gameCode}/${sanitizeURIPathSegment(setName)}/${sanitizeURISlug(row.number)}`;

  const gradeColumns: DataTableColumn<CardInfo>[] = visibleGradeColumns(showAll).map(
    (spec) => ({
      id: spec.id,
      label: spec.label,
      shortLabel: spec.short,
      /* A count: right-aligned, and largest-first on the first click. */
      numeric: true,
      sortValue: (row: CardInfo) => gradeCountValue(spec.grade, row.finalGradeCounts),
      render: (row: CardInfo) => (
        <GradePill
          count={gradeCountValue(spec.grade, row.finalGradeCounts)}
          total={row.cardsGraded ?? 0}
        >
          <CardGradeViewLink cardInfo={row} grade={spec.grade} />
        </GradePill>
      ),
      /*
        Never wrapped (D17 of docs/specs/pop-language-split.md, Robert, 2026-10-20): at
        w-20 "Flawless 10 (Quad)", "Pristine 10" and "Gem Mint 10" broke over two or three
        lines while 9.5…A sat on one. w-20 is only a floor in an auto-layout table, so with
        nowrap the three 10 columns widen to fit their label and sort icon instead of
        squeezing them, and the short ones keep their 5rem. The class is the column's, so
        it lands on the cells too, where a count on a pill never wanted to wrap anyway.
      */
      className: "whitespace-nowrap",
      /*
        Batch 4 (Robert, 2026-10-21: "make all the grades fit across this line"): the w-20
        floor is gone and the column is compact (sort icon under the label, half the side
        padding), so all sixteen grades sit across a 1440px row without scrolling. The
        labels still never wrap, per D17 above.
      */
      compact: true,
    })
  );

  const columns: DataTableColumn<CardInfo>[] = [
    {
      id: "number",
      label: "Card #",
      /* sortValue supplies the cell text; compare wins for the ordering. */
      sortValue: (row) => row.number,
      compare: compareByCardNumber<CardInfo>((row) => row.number),
      className: "w-24",
    },
    /*
      The card's art, between its number and its name (Robert, 2026-10-20, after admin's
      catalog table): hover or focus the thumbnail for a large preview. Not sortable and
      not filterable — there is nothing in a picture to order or match.
    */
    {
      id: "image",
      label: "Image",
      render: (row) => (
        <CardThumbPreview image={row.image} to={cardPath(row)} name={row.title} />
      ),
      className: "w-[84px]",
    },
    {
      id: "title",
      label: "Card Name / Details",
      sortValue: (row) => row.title,
      /*
        A custom render defaults filterText to "" because JSX cannot be matched, and the
        card name is the whole reason this table has a filter box.
      */
      filterText: (row) => `${row.title} ${row.rarity}`,
      render: (row) => (
        <>
          <Link to={cardPath(row)} className={LINK}>
            {row.title}
          </Link>
          {row.rarity ? (
            <>
              <br />
              <em className="text-pcg-textMuted">{titleCaseRarity(row.rarity)}</em>
            </>
          ) : null}
        </>
      ),
    },
    {
      id: "cardsGraded",
      label: "Total Grades",
      numeric: true,
      sortValue: (row) => row.cardsGraded ?? 0,
      render: (row) => (
        <Link to={cardPath(row)} className={LINK}>
          {formatCount(row.cardsGraded ?? 0)}
        </Link>
      ),
      className: "w-28",
    },
    ...gradeColumns,
  ];

  return columns.map((column) => ({
    ...column,
    /* Rows are as tall as the thumbnail, so every cell centres on it (align-middle wins over the shared align-top). */
    className: `${column.className ?? ""} align-middle`.trim(),
  }));
};

export interface SetViewTableProps {
  gameCode: string;
  setName: string;
  /*
    The view owns the table state because the two filter controls sit above this
    component. Taking the controller — rather than nine flattened props — is what makes
    handing pagination state.page instead of view.page impossible to express (§6.5).
  */
  table: TableController;
  /* The grade whose population must be non-zero, or "" for every card (D18). */
  filterGrade: string;
  /*
    The view's heading and filter controls, laid out in this table's top row with the Show
    All Grades button (Robert, 2026-10-20: the set page had "a lot of open space in the
    middle"). Heading left; controls and the button right; one row instead of three.
  */
  heading?: ReactNode;
  controls?: ReactNode;
}

/* One frozen empty array, so "no data yet" is not a new array identity every render. */
const NO_CARDS: CardInfo[] = [];

/* `skin-ghost`: the event skin's gold-outline button. Inert in navy and gilded. */
const SHOW_ALL_BUTTON =
  `skin-ghost inline-flex items-center rounded-md border border-pcg-border bg-pcg-card px-3 py-1.5 ` +
  `text-sm font-semibold text-pcg-goldText hover:bg-pcg-raised ${FOCUS_RING}`;

/*
  SetView seeds its table with this, and SetViewTable falls back to it when hiding the
  lower grades takes away the column the table was sorted by. One constant, so the two
  cannot drift apart.
*/
export const SET_VIEW_DEFAULT_SORT: TableSortState = {
  orderBy: "cardsGraded",
  order: "desc",
};

function SetViewTable(props: SetViewTableProps) {
  const { gameCode, setName, table, filterGrade, heading, controls } = props;

  /*
    Every grade column shows by default (Robert, 2026-10-20). With the lower grades hidden
    the table was half as wide as the page and left a band of open space beside it; the
    button now offers to hide them instead.
  */
  const [showAll, setShowAll] = useState(true);

  /*
    v1 returns products with cardsGraded and finalGradeCounts already populated. `error`
    is no longer discarded — DataTable takes it and renders the error state (§12.20).
  */
  /*
    `currentData`: with plain `data`, moving set A -> set B kept A's rows, so
    `isLoading={isFetching && cards.length === 0}` stayed false and A's cards rendered
    under B's heading with no skeleton at all.
  */
  const { currentData: data, isFetching, error } = useGetProductsWithCountsQuery(
    { gameCode, setName },
    { skip: !gameCode || !setName }
  );

  const cards = useMemo<CardInfo[]>(() => {
    if (!data) {
      return NO_CARDS;
    }
    return productsToCards(data, gameCode);
  }, [data, gameCode]);

  /*
    D18. filterCardsGraded already existed and already matched the strings FilterGradeDDL
    emits, so it is reused rather than rewritten. It is applied ONLY when a grade is
    chosen: called with an empty value it degrades to a `cardsGraded !== null` check,
    which would quietly drop cards this table has always shown.
  */
  const rows = useMemo(
    () => (filterGrade ? cards.filter(filterCardsGraded(filterGrade)) : cards),
    [cards, filterGrade]
  );

  const columns = useMemo(
    () => setViewColumns(gameCode, setName, showAll),
    [gameCode, setName, showAll]
  );

  /*
    Hiding the lower grades can take away the column the table is sorted by. sortRowsByColumn
    then finds no descriptor and returns the rows in raw API order — silently in table mode,
    but the stacked Sort-by control falls back to the first sortable column and announces
    "Sorted by Card #, A to Z" over rows that are in no such order. Fall back to the view's
    own default instead, which is a sort that still exists.
  */
  const { setSort } = table;
  const orderBy = table.state.sort.orderBy;
  const handleToggleShowAll = useCallback(() => {
    setShowAll(previous => {
      const next = !previous;
      if (!next && LOWER_GRADE_COLUMN_IDS.has(orderBy)) {
        setSort(SET_VIEW_DEFAULT_SORT);
      }
      return next;
    });
  }, [orderBy, setSort]);

  const view = useMemo(
    () => selectTableRows(rows, columns, table.state),
    [rows, columns, table.state]
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">{heading}</div>
        <div className="flex w-full flex-wrap items-end justify-end gap-3 md:w-auto">
          {controls}
          {/*
            Its label names the action and flips with the state, so it is an action button,
            not a pressed-state toggle. aria-pressed alongside a flipping label announces
            "Hide Lower Grades, toggle button, pressed" — the label and the state saying
            opposite things. GameView's mode switch keeps fixed labels and so keeps
            aria-pressed; this one states its state in words instead.
          */}
          <button
            type="button"
            onClick={handleToggleShowAll}
            className={SHOW_ALL_BUTTON}
          >
            {showAll ? "Hide Lower Grades" : "Show All Grades"}
          </button>
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={view.rows}
        getRowKey={(row) => row.id}
        caption="Cards"
        sort={table.state.sort}
        onSortChange={table.setSort}
        /* A skeleton only until the first rows land, never over rows already showing. */
        isLoading={isFetching && cards.length === 0}
        error={error}
        emptyState={
          <DataTableEmpty
            title="No cards found"
            description="No card in this set matches the current grade and search filters."
          />
        }
        /* Page-sized, so the skeleton is the shape the real page will be. */
        skeletonRows={table.state.rowsPerPage}
      />

      <DataTablePagination
        page={view.page}
        rowsPerPage={table.state.rowsPerPage}
        count={view.filteredCount}
        onChangePage={table.setPage}
        onChangeRowsPerPage={table.setRowsPerPage}
        label="Cards pagination"
      />
    </div>
  );
}

export default SetViewTable;
